/**
 * Marketing: email campaigns, the website form that turns visitors into leads, and the do-not-email
 * list every campaign respects.
 *
 * A campaign opens on its own page (`/marketing/:id`), where it is written, previewed and sent. This
 * page lists them and holds the two things every campaign depends on: the settings (the postal
 * address the footer must carry) and the list of people who asked not to be emailed.
 */
import { TextArea, type Container } from 'webwin';
import { isAdmin } from '../api/auth.js';
import * as crm from '../api/crm.js';
import { describeError } from '../api/http.js';
import * as marketing from '../api/marketing.js';
import type { CampaignDto, MarketingSettingsDto, SuppressionDto, UserDto } from '../contracts.gen.js';
import { join, localDate, localDateTime, plural } from '../format.js';
import { campaignTone } from '../leads.js';
import type { Card } from '../ui/Card.js';
import { CardList } from '../ui/CardList.js';
import { chip } from '../ui/Chip.js';
import { confirm } from '../ui/dialogs.js';
import { EmptyState } from '../ui/EmptyState.js';
import { StatusLine } from '../ui/StatusLine.js';
import {
  button,
  buttonRow,
  card,
  checkBox,
  comboBox,
  field,
  fieldRow,
  fillWith,
  FONT,
  GAP,
  h1,
  h2,
  inlineRow,
  muted,
  relayout,
  scrollPage,
  splitRow,
  stackOf,
  text,
  textBox,
  value,
  vstack
} from '../ui/layout.js';
import type { CrmPage, PageContext } from './page.js';

export function marketingPage(): CrmPage {
  return {
    async mount(host: Container, ctx: PageContext) {
      const admin = isAdmin();
      let users: UserDto[] = [];
      let settings: MarketingSettingsDto | null = null;
      let busy = false;
      const status = new StatusLine();

      const titleRow = splitRow({ align: 'center' });
      fillWith(titleRow, h1('Marketing'));
      titleRow.controls.add(button('New campaign', () => void newCampaign(), true));
      const header = stackOf(
        vstack({ spacing: GAP.xs }),
        titleRow,
        muted('Email campaigns to leads and customer contacts, sent through the mail account under Settings, one message per person.'),
        status.label
      );

      // ---- campaigns ----
      const campaigns = new CardList<CampaignDto>({ key: c => c.id, build: c => campaignCard(c) });
      const campaignsEmpty = new EmptyState('No campaigns yet. A campaign is one email, written once and sent to everyone in an audience you choose.');

      // ---- settings and the website form ----
      const postal = new TextArea({ height: 70, hAlign: 'stretch', wrap: true });
      const senderName = textBox({ placeholder: 'Blank = the mail account’s From name' });
      const formEnabled = checkBox('Accept leads from the website form', false);
      const formHeading = textBox();
      const formThanks = textBox();
      const formNotify = textBox({ placeholder: 'sales@yourmsp.com' });
      const formOwner = comboBox<string>(['Nobody'], { selectedIndex: 0 });
      const formUrl = text('');
      const formWarning = muted('', 'warn');
      const embed = text('', { font: FONT.small });
      const settingsLine = new StatusLine();
      const settingsCard = card();
      const saveRow = stackOf(
        buttonRow(),
        button('Save', () => void saveSettings(false), true),
        button('New form address', () => void saveSettings(true))
      );
      settingsCard.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          h2('Settings and the website form'),
          field('Postal address (printed at the foot of every campaign)', postal),
          muted('Bulk-mail law (CAN-SPAM, CASL, PECR) asks for a physical address in commercial email, so a campaign cannot be sent without one.'),
          field('Sender name', senderName),
          formEnabled,
          fieldRow(field('Form heading', formHeading), field('Thank-you message', formThanks)),
          fieldRow(field('Email new website leads to', formNotify), field('Owner of website leads', formOwner)),
          field('Form address', formUrl),
          formWarning,
          muted('Link to the address above, or post your own website’s form to it with fields name, company, email, phone and message:'),
          embed,
          admin ? saveRow : muted('Only an administrator can change these settings.'),
          settingsLine.label
        )
      );

      // ---- the do-not-email list ----
      const suppressAddress = textBox({ placeholder: 'name@example.com' });
      const suppressSearch = textBox({ placeholder: 'Search' });
      suppressSearch.on('keydown', (e: { key: string }) => {
        if (e.key === 'Enter') void loadSuppressions();
      });
      const suppressLine = new StatusLine();
      const suppressions = new CardList<SuppressionDto>({ key: s => s.id, build: s => suppressionCard(s) });
      const suppressEmpty = new EmptyState('Nobody has unsubscribed yet.');
      const suppressCard = card();
      suppressCard.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          h2('Do-not-email list'),
          muted('Everyone here is skipped by every campaign: people who used an unsubscribe link, addresses that bounced, and anyone added by hand.'),
          stackOf(
            buttonRow({ spacing: GAP.md, align: 'end' }),
            field('Add an address', suppressAddress, { width: 240 }),
            button('Add', () => void addSuppression()),
            field('Search', suppressSearch, { width: 200 }),
            button('Search', () => void loadSuppressions())
          ),
          suppressLine.label,
          suppressEmpty.panel,
          suppressions.panel
        )
      );

      const { view, content } = scrollPage();
      content.controls.add(
        stackOf(vstack({ spacing: GAP.lg }), header, h2('Campaigns'), campaignsEmpty.panel, campaigns.panel, settingsCard, suppressCard)
      );
      host.controls.add(view);

      function campaignCard(c: CampaignDto): Card {
        const heading = inlineRow();
        heading.controls.add(value(c.name, { font: FONT.bold }));
        heading.controls.add(chip(c.statusLabel, campaignTone(c.status)));
        const column = stackOf(vstack({ spacing: GAP.xs }), heading);
        if (c.subject) column.controls.add(muted(c.subject));
        column.controls.add(
          text(
            c.recipients === 0
              ? `Draft · edited ${localDate(c.updatedUtc)}`
              : join([
                  `${c.sent} of ${plural(c.recipients, 'recipient')} sent`,
                  c.failed > 0 ? `${c.failed} failed` : null,
                  c.pending > 0 ? `${c.pending} waiting` : null,
                  c.unsubscribes > 0 ? plural(c.unsubscribes, 'unsubscribe') : null,
                  c.leadsCreated > 0 ? `${plural(c.leadsCreated, 'lead')}, ${c.leadsWon} won` : null,
                  c.startedUtc ? `started ${localDateTime(c.startedUtc)}` : null
                ]),
            { font: FONT.small }
          )
        );
        const shell = card({ cursor: 'pointer' });
        shell.controls.add(column);
        shell.onActivate(() => ctx.go(`/marketing/${c.id}`));
        return shell;
      }

      function suppressionCard(s: SuppressionDto): Card {
        const row = splitRow({ align: 'center' });
        fillWith(
          row,
          stackOf(vstack({ spacing: 0 }), value(s.email, { font: FONT.bold }), muted(join([s.reason, localDate(s.whenUtc), s.addedBy])))
        );
        if (admin) row.controls.add(button('Remove', () => void removeSuppression(s)));
        const shell = card();
        shell.controls.add(row);
        return shell;
      }

      function renderSettings(s: MarketingSettingsDto): void {
        settings = s;
        postal.text = s.postalAddress ?? '';
        senderName.text = s.senderName ?? '';
        formEnabled.checked = s.formEnabled;
        formHeading.text = s.formHeading ?? '';
        formThanks.text = s.formThankYou ?? '';
        formNotify.text = s.formNotifyEmail ?? '';
        formOwner.selectedIndex = s.formDefaultOwnerUserId === null ? 0 : Math.max(0, users.findIndex(u => u.id === s.formDefaultOwnerUserId) + 1);
        formUrl.text = s.formUrl;
        formWarning.text = s.formUrlWarning ?? '';
        formWarning.visible = !!s.formUrlWarning;
        embed.text = `<form method="post" action="${s.formUrl}"> … </form>`;
        for (const c of [postal, senderName, formEnabled, formHeading, formThanks, formNotify, formOwner]) c.enabled = admin;
        relayout(settingsCard);
      }

      async function loadCampaigns(): Promise<void> {
        const list = await marketing.getCampaigns(ctx.dispose.signal());
        if (ctx.cancelled()) return;
        campaigns.set(list);
        campaignsEmpty.update(list.length);
        relayout(content);
      }

      async function loadSuppressions(): Promise<void> {
        try {
          const list = await marketing.getSuppressions(suppressSearch.text, ctx.dispose.signal());
          if (ctx.cancelled()) return;
          suppressions.set(list);
          suppressEmpty.update(list.length, suppressSearch.text.trim() ? 'Nobody on the list matches.' : 'Nobody has unsubscribed yet.');
          relayout(content);
        } catch (error) {
          if (!ctx.cancelled()) suppressLine.error(describeError(error));
        }
      }

      async function newCampaign(): Promise<void> {
        if (busy) return;
        busy = true;
        status.busy('Creating a draft…');
        try {
          const c = await marketing.createCampaign({
            name: `Campaign ${new Date().toLocaleDateString()}`,
            subject: '',
            body: 'Hello {FirstName},\n\n\n\n{Sender}',
            audience: { includeLeads: true, leadStatuses: [], leadTag: '', includeCustomerContacts: false, includeEmployees: false, customerIds: [] }
          });
          ctx.go(`/marketing/${c.id}`);
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        } finally {
          busy = false;
        }
      }

      async function saveSettings(rotate: boolean): Promise<void> {
        if (busy || !settings) return;
        if (rotate) {
          const ok = await confirm(ctx.desktop, {
            title: 'New form address',
            message: 'Issue a new form address? The current one stops working at once, so any website form posting to it must be updated.'
          });
          if (!ok || ctx.cancelled()) return;
        }
        busy = true;
        settingsLine.busy('Saving…');
        try {
          const ownerIndex = formOwner.selectedIndex;
          const s = await marketing.updateMarketingSettings({
            postalAddress: postal.text,
            senderName: senderName.text,
            formEnabled: formEnabled.checked,
            formHeading: formHeading.text,
            formThankYou: formThanks.text,
            formNotifyEmail: formNotify.text,
            formDefaultOwnerUserId: ownerIndex > 0 ? (users[ownerIndex - 1]?.id ?? null) : null,
            clearFormDefaultOwner: ownerIndex === 0,
            rotateFormKey: rotate
          });
          if (ctx.cancelled()) return;
          renderSettings(s);
          settingsLine.ok(rotate ? 'Issued a new form address.' : 'Saved.');
        } catch (error) {
          if (!ctx.cancelled()) settingsLine.error(describeError(error));
        } finally {
          busy = false;
        }
      }

      async function addSuppression(): Promise<void> {
        if (busy) return;
        const email = suppressAddress.text.trim();
        if (!email) {
          suppressLine.error('Type the address to add.');
          return;
        }
        busy = true;
        try {
          await marketing.addSuppression({ email, reason: null });
          if (ctx.cancelled()) return;
          suppressAddress.text = '';
          suppressLine.ok(`Added ${email}. No campaign will email it.`);
          await loadSuppressions();
        } catch (error) {
          if (!ctx.cancelled()) suppressLine.error(describeError(error));
        } finally {
          busy = false;
        }
      }

      async function removeSuppression(s: SuppressionDto): Promise<void> {
        if (busy) return;
        const ok = await confirm(ctx.desktop, {
          title: 'Remove from the list',
          message: `Take ${s.email} off the do-not-email list? ${s.reason}. Campaigns will email this address again - only do this if the person asked.`
        });
        if (!ok || ctx.cancelled()) return;
        busy = true;
        try {
          await marketing.removeSuppression(s.id);
          if (ctx.cancelled()) return;
          suppressLine.ok(`Removed ${s.email}.`);
          await loadSuppressions();
        } catch (error) {
          if (!ctx.cancelled()) suppressLine.error(describeError(error));
        } finally {
          busy = false;
        }
      }

      try {
        users = (await crm.getStaffUsers(ctx.dispose.signal())).filter(u => !u.disabled);
        formOwner.items = ['Nobody', ...users.map(u => u.displayName)];
      } catch {
        // The owner picker stays at "Nobody".
      }
      if (ctx.cancelled()) return;
      try {
        await Promise.all([loadCampaigns(), loadSuppressions(), marketing.getMarketingSettings(ctx.dispose.signal()).then(s => !ctx.cancelled() && renderSettings(s))]);
      } catch (error) {
        if (!ctx.cancelled()) status.error(describeError(error));
      }
    }
  };
}
